import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './PublicationWall.scss';
import { PostCardList, PostCardListStateType } from '../PostCardList/PostCardList';
import { CardPostsLocale } from '@/features/publication-wall/constants/labels';
import { Post } from '@/core/services/interfaces/PostInterfaces';
import { useAppLanguage } from '@/core/hooks/useAppLanguage';
import { SortBy } from '@/core/components/SortBy/SortBy/SortBy';
import { Pagination } from '@/core/components/Pagination/Pagination';
import { Loader } from '@/core/components/Loader/Loader';
import { useToast } from '@/core/components/ToastProvider/ToastProvider';
import { createRefreshDataHandler } from '@/core/utils/createRefreshDataHandler';
import { CARD_POST_EVENTS } from '@/features/publication-wall/constants/events';

const isCardLocale = (value?: string): value is CardPostsLocale => {
  return value === 'pt' || value === 'es' || value === 'en';
};

const MFE_EVENT_UPDATE_POSTS = 'mfe-card-posts-update-posts';
const MFE_EVENT_LOADED = 'mfe-card-posts-loaded';
const MFE_EVENT_LOADING = 'mfe-publication-wall-loading';
const MFE_EVENT_ERROR_LOADING = 'mfe-publication-wall-error-loading';
const MFE_EVENT_ANNOTATION_SUCCESS = 'mfe-card-posts-annotation-success';
const MFE_EVENT_FORCE_UPDATE_ERROR = 'mfe-card-posts-force-update-error';
const MFE_EVENT_FORCE_UPDATE_SUCCESS = 'mfe-card-posts-force-update-success';
const MFE_EVENT_FORCE_UPDATE_REQUEST_ERROR = 'mfe-card-posts-force-update-request-error';
const MFE_EVENT_POST_NOT_FOUND = 'mfe-card-posts-post-not-found';
const MFE_EVENT_NO_POSTS_FOUND = 'mfe-card-posts-no-posts-found';
const MFE_EVENT_BAN_USER_LOADING = 'mfe-card-posts-ban-user-loading';
const MFE_EVENT_BAN_USER_SUCCESS = 'mfe-card-posts-ban-user-success';
const MFE_EVENT_BAN_USER_ID_ERROR = 'mfe-card-posts-ban-user-id-error';
const MFE_EVENT_COPY_ID_SUCCESS = 'mfe-card-posts-copy-id-success';
const MFE_EVENT_COPY_ID_ERROR = 'mfe-card-posts-copy-id-error';
const MFE_EVENT_COPY_LINK_SUCCESS = 'mfe-card-posts-copy-link-success';
const MFE_EVENT_COPY_LINK_ERROR = 'mfe-card-posts-copy-link-error';
const MFE_EVENT_EXPORT_FILTER_ERROR = 'mfe-card-posts-export-filter-error';
const MFE_EVENT_EXPORT_PROCESSING = 'mfe-card-posts-export-processing';
const MFE_EVENT_INITIAL_NOTIFICATION = 'mfe-card-posts-initial-notification';
const MFE_HOST_TIMEOUT_MS = 10_000;

export interface PublicationWallProps {
  posts?: Post[];
  existingTags?: string[];
  onRetry?: () => void;
}

export const PublicationWall: React.FC<PublicationWallProps> = ({
  posts: propPosts = [],
  existingTags: propExistingTags = [],
  onRetry = () => window.location.reload(),
}) => {
  const { t } = useTranslation();
  const { locale } = useAppLanguage();
  const resolvedLocale = isCardLocale(locale) ? locale : 'pt';
  const { showToast } = useToast();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [eventPosts, setEventPosts] = useState<Post[]>([]);
  const [eventTotalCount, setEventTotalCount] = useState<number | null>(null);
  const [eventExistingTags, setEventExistingTags] = useState<string[]>([]);
  const [isUsingEventData, setIsUsingEventData] = useState(false);
  const [isLoading, setIsLoading] = useState(propPosts.length === 0);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const hasDispatchedLoadedRef = useRef(false);

  // Prefer event data after the first payload, even when the page has zero items.
  const posts = isUsingEventData ? eventPosts : propPosts;
  const totalItems = isUsingEventData ? (eventTotalCount ?? eventPosts.length) : propPosts.length;
  const existingTags = isUsingEventData ? eventExistingTags : propExistingTags;

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!isOffline) return;

    const retryTimeout = setTimeout(() => {
      onRetry();
    }, 30_000);

    return () => clearTimeout(retryTimeout);
  }, [isOffline, onRetry]);

  const makeToastHandler = useCallback(
    (variant: 'success' | 'warning' | 'error', titleKey: string, icon: string) =>
      () => {
        showToast({ variant, title: t(titleKey), text: '', icon }).catch((err: unknown) => {
          console.error('[PublicationWall] Failed to show toast:', err);
        });
      },
    [showToast, t],
  );

  useEffect(() => {
    // Listen for post updates from the host
    const handleUpdatePosts = (event: Event) => {
      const { posts: newPosts, totalCount, existingTags, error: hasErrorResponse } = (event as CustomEvent<{ 
        posts: Post[]; 
        totalCount?: number;
        existingTags?: string[];
        error?: boolean;
      }>).detail ?? {};

      if (hasErrorResponse) {
        setIsLoading(false);
        setHasTimedOut(false);
        setHasError(true);
        setIsUsingEventData(true);
        setEventPosts([]);
        return;
      }

      if (Array.isArray(newPosts)) {
        setIsLoading(false);
        setHasTimedOut(false);
        setHasError(false);
        setIsUsingEventData(true);
        setEventPosts(newPosts);
      }
      if (typeof totalCount === 'number' && Number.isFinite(totalCount)) {
        setEventTotalCount(totalCount);
      }
      if (Array.isArray(existingTags)) {
        setEventExistingTags(existingTags);
      }
    };

    // Listen for explicit loading state from the host
    const handleLoading = (event: Event) => {
      const customEvent = event as CustomEvent<{ isLoading: boolean }>;
      if (customEvent.detail && typeof customEvent.detail.isLoading === 'boolean') {
        const newIsLoading = customEvent.detail.isLoading;
        setIsLoading(newIsLoading);
        if (newIsLoading) {
          setHasTimedOut(false);
          setHasError(false);
        }
      }
    };

    const hostTimeout = setTimeout(() => {
      setIsLoading((currentIsLoading) => {
        if (currentIsLoading) {
          setHasTimedOut(true);
          setHasError(false);
        }
        return false;
      });
    }, MFE_HOST_TIMEOUT_MS);

    const handleAnnotationSuccess      = makeToastHandler('success', 'notifications.annotationSuccess', 'checkball');
    const handleForceUpdateError       = makeToastHandler('warning', 'notifications.forceUpdateError', 'error');
    const handleForceUpdateSuccess     = makeToastHandler('success', 'notifications.forceUpdateSuccess', 'checkball');
    const handleForceUpdateRequestError= makeToastHandler('warning', 'notifications.forceUpdateRequestError', 'error');

    const handleRefreshData = createRefreshDataHandler(
      MFE_EVENT_FORCE_UPDATE_SUCCESS,
      MFE_EVENT_FORCE_UPDATE_REQUEST_ERROR,
    );
    const handlePostNotFound           = makeToastHandler('warning', 'notifications.postNotFound', 'error');
    const handleNoPostsFound           = makeToastHandler('warning', 'notifications.noPostsFound', 'warning');
    const handleBanUserLoading         = makeToastHandler('warning', 'notifications.banUserLoading', 'error');
    const handleBanUserSuccess         = makeToastHandler('success', 'notifications.banUserSuccess', 'checkball');
    const handleBanUserIdError         = makeToastHandler('warning', 'notifications.banUserIdError', 'error');
    const handleCopyIdSuccess          = makeToastHandler('success', 'notifications.copyIdSuccess', 'checkball');
    const handleCopyIdError            = makeToastHandler('warning', 'notifications.copyIdError', 'error');
    const handleCopyLinkSuccess        = makeToastHandler('success', 'notifications.copyLinkSuccess', 'checkball');
    const handleCopyLinkError          = makeToastHandler('warning', 'notifications.copyLinkError', 'error');
    const handleExportFilterError      = makeToastHandler('warning', 'notifications.exportFilterError', 'error');
    const handleExportProcessing       = makeToastHandler('warning', 'notifications.exportProcessing', 'error');
    const handleInitialNotification    = makeToastHandler('warning', 'notifications.initialNotification', 'error');

    const handleHostError = () => {
      setIsLoading(false);
      setHasTimedOut(false);
      setHasError(true);
      setIsUsingEventData(true);
      setEventPosts([]);
    };

    document.addEventListener(MFE_EVENT_UPDATE_POSTS, handleUpdatePosts);
    document.addEventListener(MFE_EVENT_LOADING, handleLoading);
    document.addEventListener(MFE_EVENT_ERROR_LOADING, handleHostError);
    document.addEventListener(MFE_EVENT_ANNOTATION_SUCCESS, handleAnnotationSuccess);
    document.addEventListener(CARD_POST_EVENTS.REFRESH_DATA, handleRefreshData);
    document.addEventListener(MFE_EVENT_FORCE_UPDATE_ERROR, handleForceUpdateError);
    document.addEventListener(MFE_EVENT_FORCE_UPDATE_SUCCESS, handleForceUpdateSuccess);
    document.addEventListener(MFE_EVENT_FORCE_UPDATE_REQUEST_ERROR, handleForceUpdateRequestError);
    document.addEventListener(MFE_EVENT_POST_NOT_FOUND, handlePostNotFound);
    document.addEventListener(MFE_EVENT_NO_POSTS_FOUND, handleNoPostsFound);
    document.addEventListener(MFE_EVENT_BAN_USER_LOADING, handleBanUserLoading);
    document.addEventListener(MFE_EVENT_BAN_USER_SUCCESS, handleBanUserSuccess);
    document.addEventListener(MFE_EVENT_BAN_USER_ID_ERROR, handleBanUserIdError);
    document.addEventListener(MFE_EVENT_COPY_ID_SUCCESS, handleCopyIdSuccess);
    document.addEventListener(MFE_EVENT_COPY_ID_ERROR, handleCopyIdError);
    document.addEventListener(MFE_EVENT_COPY_LINK_SUCCESS, handleCopyLinkSuccess);
    document.addEventListener(MFE_EVENT_COPY_LINK_ERROR, handleCopyLinkError);
    document.addEventListener(MFE_EVENT_EXPORT_FILTER_ERROR, handleExportFilterError);
    document.addEventListener(MFE_EVENT_EXPORT_PROCESSING, handleExportProcessing);
    document.addEventListener(MFE_EVENT_INITIAL_NOTIFICATION, handleInitialNotification);

    // Dispatch loaded event to signal we're ready to receive posts (only once)
    if (!hasDispatchedLoadedRef.current) {
      hasDispatchedLoadedRef.current = true;
      document.dispatchEvent(
        new CustomEvent(MFE_EVENT_LOADED, { detail: { requestType: 'watch_mentions' } }),
      );
    }

    return () => {
      document.removeEventListener(MFE_EVENT_UPDATE_POSTS, handleUpdatePosts);
      document.removeEventListener(MFE_EVENT_LOADING, handleLoading);
      document.removeEventListener(MFE_EVENT_ERROR_LOADING, handleHostError);
      document.removeEventListener(MFE_EVENT_ANNOTATION_SUCCESS, handleAnnotationSuccess);
      document.removeEventListener(CARD_POST_EVENTS.REFRESH_DATA, handleRefreshData);
      document.removeEventListener(MFE_EVENT_FORCE_UPDATE_ERROR, handleForceUpdateError);
      document.removeEventListener(MFE_EVENT_FORCE_UPDATE_SUCCESS, handleForceUpdateSuccess);
      document.removeEventListener(MFE_EVENT_FORCE_UPDATE_REQUEST_ERROR, handleForceUpdateRequestError);
      document.removeEventListener(MFE_EVENT_POST_NOT_FOUND, handlePostNotFound);
      document.removeEventListener(MFE_EVENT_NO_POSTS_FOUND, handleNoPostsFound);
      document.removeEventListener(MFE_EVENT_BAN_USER_LOADING, handleBanUserLoading);
      document.removeEventListener(MFE_EVENT_BAN_USER_SUCCESS, handleBanUserSuccess);
      document.removeEventListener(MFE_EVENT_BAN_USER_ID_ERROR, handleBanUserIdError);
      document.removeEventListener(MFE_EVENT_COPY_ID_SUCCESS, handleCopyIdSuccess);
      document.removeEventListener(MFE_EVENT_COPY_ID_ERROR, handleCopyIdError);
      document.removeEventListener(MFE_EVENT_COPY_LINK_SUCCESS, handleCopyLinkSuccess);
      document.removeEventListener(MFE_EVENT_COPY_LINK_ERROR, handleCopyLinkError);
      document.removeEventListener(MFE_EVENT_EXPORT_FILTER_ERROR, handleExportFilterError);
      document.removeEventListener(MFE_EVENT_EXPORT_PROCESSING, handleExportProcessing);
      document.removeEventListener(MFE_EVENT_INITIAL_NOTIFICATION, handleInitialNotification);
      clearTimeout(hostTimeout);
    };
  }, [makeToastHandler, showToast, t]);

  // Paginate the posts
  const paginatedPosts = useMemo(() => {
    if (isUsingEventData) {
      return posts;
    }

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return posts.slice(startIndex, endIndex);
  }, [currentPage, itemsPerPage, posts, isUsingEventData]);

  useEffect(() => {
    // Listen for pagination change events from the Pagination component
    const handleEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail) {
        const { page, limit } = customEvent.detail;
        setCurrentPage(page);
        setItemsPerPage(limit);
      }
    };

    window.addEventListener('mfe-pagination-change', handleEvent);
    return () => window.removeEventListener('mfe-pagination-change', handleEvent);
  }, []);

  const stateType = useMemo((): PostCardListStateType => {
    if (isOffline) return 'offline';
    if (hasError) return 'error';
    if (hasTimedOut) return 'loadingTime';
    return 'noResults';
  }, [isOffline, hasError, hasTimedOut]);

  return (
    <div className="publication-wall">
      <h1 className="publication-wall__title">{t('menu.publicationWallTitle')}</h1>

      {/* Sort By component - placed right below the title */}
      <SortBy />

      {isLoading ? (
        <Loader />
      ) : (
        <>
          {/* Pagination component - placed below the sortby and before the cards */}
          {posts.length > 0 && !isOffline && (
            <Pagination
              totalItems={totalItems}
              initialPage={currentPage}
              initialLimit={itemsPerPage}
            />
          )}

          {/* Post cards list */}
          <PostCardList 
            posts={isOffline ? [] : paginatedPosts} 
            locale={resolvedLocale} 
            existingTags={existingTags} 
            stateType={stateType} 
          />

          {/* Pagination component - placed below the cards and at the footer */}
          {posts.length > 0 && !isOffline && (
            <Pagination
              totalItems={totalItems}
              initialPage={currentPage}
              initialLimit={itemsPerPage}
            />
          )}
        </>
      )}
    </div>
  );
}